import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Input } from "antd";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import decodeVinFromNhtsa from "../../utils/decodeVinNhtsa";
import { useToast } from "../../../../contexts/ToastContext";

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

const VinVerifyStep = ({
    translationNamespace = "cars",
    translationKeyPrefix = "",
    onContinue,
    onManualEntry,
    hideContinueButton = false,
    vinInput: controlledVin,
    setVinInput: setControlledVin,
    vinVerifying: controlledVerifying,
    vinBannerError: controlledBannerError,
    decoded: controlledDecoded,
    onVerify,
    onEditVin,
    showDecodedSummary = true,
    variant = "default",
}) => {
    const { t } = useTranslation(translationNamespace);
    const toast = useToast();
    const tk = (key) => (translationKeyPrefix ? t(`${translationKeyPrefix}.${key}`) : t(key));

    const isControlled = controlledVin !== undefined;

    const [internalVin, setInternalVin] = useState("");
    const [internalVerifying, setInternalVerifying] = useState(false);
    const [internalDecoded, setInternalDecoded] = useState(null);
    const [internalBannerError, setInternalBannerError] = useState(null);

    const vinInput = isControlled ? controlledVin : internalVin;
    const setVinInput = isControlled ? setControlledVin : setInternalVin;
    const vinVerifying = isControlled ? controlledVerifying : internalVerifying;
    const vinBannerError = isControlled ? controlledBannerError : internalBannerError;
    const decoded = isControlled ? controlledDecoded : internalDecoded;
    const isOnboarding = variant === "onboarding";

    const handleVerifyVin = async () => {
        if (onVerify) {
            onVerify();
            return;
        }

        const clean = vinInput.trim().toUpperCase();
        if (!VIN_PATTERN.test(clean)) {
            toast.error({
                message: tk("vinInvalidTitle"),
                description: tk("vinInvalidDescription"),
            });
            return;
        }
        setInternalVerifying(true);
        setInternalBannerError(null);
        try {
            const result = await decodeVinFromNhtsa(clean);
            setInternalDecoded(result);
        } catch {
            setInternalDecoded(null);
            setInternalBannerError(isOnboarding ? tk("enterManuallyLink") : tk("vinDecodeFailed"));
        } finally {
            setInternalVerifying(false);
        }
    };

    useEffect(() => {
        if (!isOnboarding || isControlled) return undefined;
        const clean = vinInput.trim().toUpperCase();
            if (clean.length === 17 && !VIN_PATTERN.test(clean)) {
                setInternalBannerError(tk("enterManuallyLink"));
                return undefined;
            }
            if (!VIN_PATTERN.test(clean) || decoded || vinBannerError) return undefined;

        const timer = setTimeout(() => {
            handleVerifyVin();
        }, 400);

        return () => clearTimeout(timer);
        // Auto-verify once a complete VIN is entered on the onboarding layout.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vinInput, isOnboarding, isControlled, decoded, vinBannerError]);

    const handleManualEntry = () => {
        onManualEntry(vinInput.trim().toUpperCase() || "");
    };

    const handleContinue = () => {
        if (!decoded) return;
        onContinue(decoded);
    };

    const notAvailable =
        translationNamespace === "cars" ? t("notAvailable") : t("common.notAvailable");

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-base font-semibold text-gray-900">{tk("vinSectionLabel")}</h3>
                <p className="mt-1 text-sm text-gray-600">{tk("vinSectionDescription")}</p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-stretch">
                    <Input
                        size="large"
                        placeholder={tk("vinPlaceholder")}
                        value={vinInput}
                        maxLength={17}
                        onChange={(e) => {
                            setVinInput(e.target.value.toUpperCase());
                            if (!isControlled) {
                                setInternalBannerError(null);
                                if (internalDecoded) setInternalDecoded(null);
                            }
                        }}
                        className="h-11 rounded-lg font-mono tracking-wide sm:flex-1"
                        disabled={vinVerifying}
                    />
                    {isOnboarding ? null : (
                        <Button
                            type="primary"
                            size="large"
                            loading={vinVerifying}
                            onClick={handleVerifyVin}
                            className="shrink-0 rounded-xl border-none bg-[#5d5dff] hover:bg-[#4e4ee8] sm:w-auto">
                            {tk("verifyVin")}
                        </Button>
                    )}
                </div>
                {vinBannerError ? (
                    <p className={`mt-3 flex items-start gap-2 text-sm ${isOnboarding ? "text-red-500" : "text-amber-700"}`}>
                        <InfoCircleOutlined className="mt-0.5 shrink-0" />
                        <span>{vinBannerError}</span>
                    </p>
                ) : null}
                {isOnboarding && vinBannerError ? (
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleManualEntry}
                        className="mt-4 h-11 rounded-lg border-none bg-[#5d5dff] hover:!bg-[#4e4ee8]">
                        {tk("addManually")}
                    </Button>
                ) : null}
                {isOnboarding ? null : (
                    <button
                        type="button"
                        onClick={handleManualEntry}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#5d5dff] hover:text-[#4b4bd9]">
                        <InfoCircleOutlined />
                        {tk("enterManuallyLink")}
                    </button>
                )}
            </div>

            {showDecodedSummary && decoded && !hideContinueButton ? (
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-base font-semibold text-gray-900">{tk("vehicleDetailsTitle")}</h3>
                        {onEditVin ? (
                            <button
                                type="button"
                                onClick={onEditVin}
                                className="text-sm font-semibold text-[#5d5dff] hover:text-[#4b4bd9]">
                                {tk("editVinDetails")}
                            </button>
                        ) : null}
                    </div>
                    <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-emerald-800">
                        <CheckCircleOutlined />
                        <span className="text-sm font-medium">{tk("vinVerified")}</span>
                    </div>
                    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {tk("make")}
                            </dt>
                            <dd className="mt-1 text-sm font-semibold text-gray-900">{decoded.make}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {tk("model")}
                            </dt>
                            <dd className="mt-1 text-sm font-semibold text-gray-900">{decoded.model}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {tk("year")}
                            </dt>
                            <dd className="mt-1 text-sm font-semibold text-gray-900">{decoded.year}</dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {tk("engineTrimLabel")}
                            </dt>
                            <dd className="mt-1 text-sm font-semibold text-gray-900">
                                {[decoded.trany, decoded.fueltype1].filter(Boolean).join(" · ") ||
                                    notAvailable}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                {tk("fuelType")}
                            </dt>
                            <dd className="mt-1 text-sm font-semibold text-gray-900">
                                {decoded.fueltype1 || notAvailable}
                            </dd>
                        </div>
                        {decoded.vclass ? (
                            <div>
                                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    {tk("bodyStyle")}
                                </dt>
                                <dd className="mt-1 text-sm font-semibold text-gray-900">{decoded.vclass}</dd>
                            </div>
                        ) : null}
                    </dl>
                    <Button
                        type="primary"
                        size="large"
                        className="mt-6 rounded-xl border-none bg-[#5d5dff] hover:bg-[#4e4ee8]"
                        onClick={handleContinue}>
                        {tk("continueWithVehicle")}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

VinVerifyStep.propTypes = {
    translationNamespace: PropTypes.string,
    translationKeyPrefix: PropTypes.string,
    onContinue: PropTypes.func,
    onManualEntry: PropTypes.func.isRequired,
    hideContinueButton: PropTypes.bool,
    vinInput: PropTypes.string,
    setVinInput: PropTypes.func,
    vinVerifying: PropTypes.bool,
    vinBannerError: PropTypes.string,
    decoded: PropTypes.object,
    onVerify: PropTypes.func,
    onEditVin: PropTypes.func,
    showDecodedSummary: PropTypes.bool,
    variant: PropTypes.oneOf(["default", "onboarding"]),
};

export default VinVerifyStep;
